// Small domain components: status, alarm and role badges, asset type names.

import type { AlarmSummary, AssetStatusCode, AssetType, MemberRole } from '@cck/schema';
import { useI18n } from '../i18n/index.ts';
import { Badge } from './ui.tsx';

const STATUS_TONE = { alarm: 'red', offline: 'violet', warning: 'amber', ok: 'green', no_data: 'slate' } as const;

export function StatusBadge({ status }: { status: AssetStatusCode }) {
  const { t } = useI18n();
  return (
    <Badge tone={STATUS_TONE[status]}>
      <span aria-hidden className={`h-1.5 w-1.5 rounded-full ${status === 'alarm' ? 'animate-pulse bg-red-600' : 'bg-current'}`} />
      {t(`status.${status}`)}
    </Badge>
  );
}

/** Left border colour of a card by status. */
export const STATUS_BORDER: Record<AssetStatusCode, string> = {
  alarm: 'border-l-red-600', offline: 'border-l-violet-500', warning: 'border-l-amber-400', ok: 'border-l-emerald-500',
  no_data: 'border-l-slate-300',
};

/** An equipment's type: a custom type shows its own name (type `other`, `label`). */
export function AssetTypeLabel({ type, label }: { type: AssetType; label?: string | null | undefined }) {
  const { t } = useI18n();
  return <>{type === 'other' && label ? label : t(`assetType.${type}`)}</>;
}

export function SeverityBadge({ severity }: { severity: 'warning' | 'critical' }) {
  const { t } = useI18n();
  return <Badge tone={severity === 'critical' ? 'red' : 'amber'}>{t(`severity.${severity}`)}</Badge>;
}

export function AlarmStateBadge({ alarm }: { alarm: Pick<AlarmSummary, 'state' | 'condition'> }) {
  const { t } = useI18n();
  if (alarm.state === 'resolved') return <Badge tone="green">{t('alarmState.resolved')}</Badge>;
  if (alarm.state === 'acknowledged') return <Badge tone="blue">{t('alarmState.acknowledged')}</Badge>;
  if (alarm.condition === 'cleared') return <Badge tone="amber">{t('alarmState.awaitingAck')}</Badge>;
  return <Badge tone="red">{t('alarmState.active')}</Badge>;
}

export function RoleLabel({ role }: { role: MemberRole }) {
  const { t } = useI18n();
  return <>{t(`role.${role}`)}</>;
}
